import { useCallback, useMemo, useState } from 'react';

import { useLazyGetBranchWorkTimesOnDateQuery } from '@app/api/Branches';
import type { TimeFormat } from '@app/api/Branches/types';
import { useLazyGetEmployeesRecordsQuery } from '@app/api/Records/api';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import {
  mapEmployeesRecordsResponse,
  extractTime,
} from '@pages/Calendar/utils';

import type { CalendarEvent, Employee } from '@pages/Calendar/data';
import { DEFAULT_END_TIME, DEFAULT_START_TIME } from '@pages/Calendar/data';

import { useCalendarContext } from '../context';

interface UseCalendarDataResult {
  employees: Employee[];
  events: CalendarEvent[];
  isLoading: boolean;
  loadedDate: string | null;
  branchStartTime: string;
  branchEndTime: string;
  // Формат журнала филиала: тем же ответом, что и рабочее время (issue/11162).
  timeFormat: TimeFormat | null;
  loadTeamJournal(date: string): Promise<void>;
  updateEventsFromSocket(employees: Employee[], events: CalendarEvent[]): void;
}

export const useCalendarData = (): UseCalendarDataResult => {
  const {
    params: { ActiveBranchId, UserId },
  } = useUserPrint();
  const {
    userAccessRights: {
      Records: { IsViewAllRecords },
    },
  } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const [getEmployeesRecords] = useLazyGetEmployeesRecordsQuery();
  const [getBranchWorkTimes] = useLazyGetBranchWorkTimesOnDateQuery();

  // Without the "view all records" right a user (e.g. a master) may only see
  // their own schedule. The HTTP journal load is already filtered by the
  // backend, but the OnJournalRecords socket broadcasts the whole branch team.
  // Enforce the same restriction on the frontend for every state update so a
  // socket event can never leak other masters' schedules into the journal.
  const restrictToOwnRecords = useCallback(
    (
      nextEmployees: Employee[],
      nextEvents: CalendarEvent[]
    ): { employees: Employee[]; events: CalendarEvent[] } => {
      if (IsViewAllRecords) {
        return { employees: nextEmployees, events: nextEvents };
      }

      const ownId = String(UserId);
      const ownEmployees = nextEmployees.filter(
        (employee) => employee.id === ownId
      );
      const ownEmployeeIds = new Set(
        ownEmployees.map((employee) => employee.id)
      );

      return {
        employees: ownEmployees,
        events: nextEvents.filter((event) =>
          ownEmployeeIds.has(event.employeeId)
        ),
      };
    },
    [IsViewAllRecords, UserId]
  );

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [timeFormat, setTimeFormat] = useState<TimeFormat | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedDate, setLoadedDate] = useState<string | null>(null);
  const {
    branchStartTime,
    branchEndTime,
    setBranchStartTime,
    setBranchEndTime,
  } = useCalendarContext();

  const loadTeamJournal = useCallback(
    async (date: string) => {
      if (!ActiveBranchId) return;

      setIsLoading(true);
      try {
        const [recordsResponse, workTimesResponse] = await Promise.all([
          getEmployeesRecords({
            date,
            ActiveBranchId,
          }).unwrap(),
          getBranchWorkTimes({
            branchId: ActiveBranchId,
            dateLocal: date,
            ActiveBranchId,
          }).unwrap(),
        ]);

        const mapped = mapEmployeesRecordsResponse(recordsResponse);
        const { employees, events } = restrictToOwnRecords(
          mapped.employees,
          mapped.events
        );

        setEmployees(employees);
        setEvents(events);
        setLoadedDate(date);

        if (workTimesResponse) {
          const { WorkTimeS, WorkTimeE, JournalFormatTime } = workTimesResponse;

          setTimeFormat(JournalFormatTime ?? null);
          setBranchStartTime(
            WorkTimeS ? extractTime(WorkTimeS) : DEFAULT_START_TIME
          );
          setBranchEndTime(
            WorkTimeE ? extractTime(WorkTimeE) : DEFAULT_END_TIME
          );
        }
      } catch (error) {
        showNotification(error);
        setEmployees([]);
        setEvents([]);
        setLoadedDate(date);
      } finally {
        setIsLoading(false);
      }
    },
    [
      ActiveBranchId,
      getEmployeesRecords,
      getBranchWorkTimes,
      showNotification,
      setBranchStartTime,
      setBranchEndTime,
      restrictToOwnRecords,
    ]
  );

  const updateEventsFromSocket = useCallback(
    (newEmployees: Employee[], newEvents: CalendarEvent[]) => {
      const { employees, events } = restrictToOwnRecords(
        newEmployees,
        newEvents
      );
      setEmployees(employees);
      setEvents(events);
    },
    [restrictToOwnRecords]
  );

  const data = useMemo(
    () => ({
      employees,
      events,
      isLoading,
      loadedDate,
      branchStartTime,
      branchEndTime,
      timeFormat,
      loadTeamJournal,
      updateEventsFromSocket,
    }),
    [
      employees,
      events,
      isLoading,
      loadedDate,
      branchStartTime,
      branchEndTime,
      timeFormat,
      loadTeamJournal,
      updateEventsFromSocket,
    ]
  );

  return data;
};
